import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import type { Channel, Media } from 'lib-shared-types-channels';
import { Component, Fragment } from 'react';

import { getChannelName } from 'utils/channels';
import { mediaAttributes, MetaWorkingMedia } from 'utils/channels/media';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { listToAPStyleComma } from 'utils/formatting/string';
import { requestMicrophoneAccess } from 'utils/telephony/microphone';

import type { ProfileProps } from '../containers/profile';

import styles from './profile.module.scss';

type MediaChannelBadgesProps = {
  metaMedia: MetaWorkingMedia;
  hasAccess: boolean;
} & Pick<
  ProfileProps,
  | 'presence'
  | 'presenceMeta'
  | 'telephony'
  | 'presenceSetMedia'
  | 'errorMicrophoneErrorShowModal'
  | 'personalToggleRememberMicrophoneAccess'
>;
type StateT = {
  loading: boolean;
};

class MediaChannelBadges extends Component<MediaChannelBadgesProps, StateT> {
  constructor(props: MediaChannelBadgesProps) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  handlePresenceSetMedia(media: Media): void {
    const { metaMedia } = this.props;
    const working = !metaMedia.working;
    this.setState({ loading: true });

    this.props
      .presenceSetMedia({ media, working })
      .then(() => {
        this.setState({ loading: false });

        ampli.presenceMediaUpdated({
          media,
          enabled: working,
          location: 'topbar',
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  @boundMethod
  onChangeHandler(): void {
    const { hasAccess, metaMedia, telephony } = this.props;
    if (!hasAccess) {
      return;
    }

    if (metaMedia.name === 'speak' && telephony.isMicrophoneBlocked) {
      requestMicrophoneAccess()
        .then(this.props.errorMicrophoneErrorShowModal)
        .catch(this.props.errorMicrophoneErrorShowModal)
        .finally(() => {
          // Remove the remember my microphone access.
          this.props.personalToggleRememberMicrophoneAccess({
            value: false,
          });
        });
    } else {
      this.handlePresenceSetMedia(metaMedia.name);
    }
  }

  getChannelListTextVerb(channels: readonly Channel[]): string {
    if (channels.length === 1) return 'is';
    if (channels.length > 1) return `are`;
    return '';
  }

  getChannelStatusText(
    channels: readonly Channel[],
    statusText: string,
  ): string {
    return channels.length
      ? `${listToAPStyleComma(
          channels.map(getChannelName),
        )} ${this.getChannelListTextVerb(channels)} ${statusText}`
      : '';
  }

  getTooltipText(): string | Array<string | JSX.Element> {
    if (!this.props.hasAccess) {
      return 'Restricted by organization';
    }

    const { metaMedia, presence, telephony } = this.props;
    const { name: mediaName, onChannels, offChannels } = metaMedia;
    const { status } = presence;

    if (mediaName === 'speak') {
      if (telephony.isMicrophoneBlocked) {
        return 'Microphone is blocked. Phone, Callback and voicemail are therefore turned off. Click to enable.';
      }
      if (!telephony.isReady) {
        return telephony.meta.asyncStatus === 'loading'
          ? 'Loading...'
          : 'Phone error';
      }
    }

    const onStatusText =
      status === 'away'
        ? 'inactive because your status is away.'
        : 'turned on.';
    const workingString = this.getChannelStatusText(onChannels, onStatusText);
    const offString = this.getChannelStatusText(offChannels, 'turned off.');

    const interleave = <T,>(arr: string[], thing: T) =>
      ([] as Array<string | T>)
        .concat(...arr.map((n) => [n, thing]))
        .slice(0, -1);

    // Filter out any empty statusText and interleave with breaklines.
    return interleave<JSX.Element>(
      [workingString, offString].filter(Boolean),
      <br />,
    ).map((content, i) => <Fragment key={i}>{content}</Fragment>);
  }

  getChannelAsyncStatus(): AsyncStatus {
    const { metaMedia, presenceMeta } = this.props;
    switch (metaMedia.name) {
      case 'interactivechat':
        return presenceMeta.chatAsyncStatus;
      case 'speak':
        return presenceMeta.voiceAsyncStatus;
      case 'email':
        return presenceMeta.emailAsyncStatus;
      case 'messaging':
        return presenceMeta.messagingAsyncStatus;
      default:
        return 'failed';
    }
  }

  render() {
    const flags = featureFlags.get(['new-topbar']);

    const { metaMedia, presence, telephony } = this.props;
    const { status } = presence;

    const defaultColor = flags.newTopbar
      ? 'var(--sidebar-text-color-secondary)'
      : 'var(--dixa-color-neutral-300)';

    let iconColor = defaultColor;
    if (status !== 'offline' && metaMedia.working) {
      iconColor =
        status === 'away'
          ? 'var(--dixa-color-orange-400)'
          : 'var(--dixa-color-green-400)';
    }

    const isTelephony = metaMedia.name === 'speak';
    if (isTelephony) {
      if (telephony.isMicrophoneBlocked) {
        iconColor = 'var(--dixa-color-red-400)';
      } else if (!telephony.isReady) {
        iconColor =
          telephony.meta.asyncStatus === 'loading'
            ? 'var(--dixa-color-blue-200)'
            : 'var(--dixa-color-red-400)';
      }
    }

    const iconClassName = classnames(styles.toggle, {
      [styles.toggleAnimated]:
        this.state.loading === true ||
        this.getChannelAsyncStatus() === 'loading',
    });

    return (
      <Tooltip content={this.getTooltipText()} wrap="none">
        <div className={iconClassName} onClick={this.onChangeHandler}>
          <Icon
            name={
              mediaAttributes[metaMedia.name][
                flags.newTopbar ? 'icon' : 'legacyIcon'
              ]
            }
            size={24}
            color={iconColor}
          />
        </div>
      </Tooltip>
    );
  }
}

export default MediaChannelBadges;
